import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ChevronDown, Play, ExternalLink, ArrowRight } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { followOnSpotify } from "../lib/spotify";
import { track } from "../lib/analytics";
import { SpotifyIcon, YouTubeIcon } from "./icons/BrandIcons";

const HERO_IMG =
  "https://customer-assets-agu9un31.emergentagent.net/job_dj-codigo-live/artifacts/or0krawi_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2002_42_23%20PM.png";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

function CampaignCta({ campaign, featured }) {
  const { playRelease } = usePlayer();
  const isWatch = campaign?.ctaType === "watch";
  const release = campaign?.release || featured;
  const label = campaign?.ctaLabel || (isWatch ? "Watch Video" : "Listen Now");
  const title = campaign?.title || featured?.title;
  if (!campaign && !featured) return null;

  const onClick = () => {
    track("campaign_cta_click", { releaseId: release?.internalId, campaignSlug: campaign?.slug || null, metadata: { placement: "hero", ctaType: isWatch ? "watch" : "listen" } });
    if (isWatch && (campaign?.videoUrl || campaign?.youtubeURL)) {
      window.open(campaign.videoUrl || campaign.youtubeURL, "_blank", "noopener,noreferrer");
    } else if (release) {
      playRelease(release);
    } else if (campaign?.spotifyURL) {
      window.open(campaign.spotifyURL, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div data-testid="hero-campaign" className="mt-8 max-w-xl">
      <p className="mb-2 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.25em] text-gold">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gold" /> {campaign?.headline || (featured?.isNew ? "New Release" : "Latest Release")}
      </p>
      <p className="font-display text-3xl font-bold uppercase leading-none tracking-tight text-white sm:text-5xl">{title}</p>
      <div className="mt-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
        <button
          data-testid="hero-campaign-cta"
          onClick={onClick}
          className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-4 text-sm font-bold uppercase tracking-wide text-black shadow-lg shadow-gold/20 transition-transform active:scale-95 hover:bg-gold-2"
        >
          {isWatch ? <YouTubeIcon size={18} /> : <Play size={16} fill="currentColor" />} {label}
        </button>
        {campaign?.slug && (
          <Link data-testid="hero-campaign-link" to={`/${campaign.slug}`} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-white/60 transition-colors hover:text-gold">
            Full story <ArrowRight size={14} />
          </Link>
        )}
      </div>
    </div>
  );
}

export default function Hero({ genreLine, tagline = "Dominican Roots. Global Sound.", image, featured, campaign, spotifyArtistUrl }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-[100svh] w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={image || HERO_IMG} alt="DJ Código — Dominican Latin urban artist, reggaetón and hip-hop DJ and producer" className="h-full w-full object-cover" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/70 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-transparent to-ink/40" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 flex min-h-[100svh] flex-col justify-end px-6 pb-40 pt-28 sm:px-10 lg:px-16">
        <motion.h1 initial="hidden" animate="show" className="font-display font-bold uppercase leading-[0.95] tracking-tighter text-white">
          <span className="mask-reveal">
            <motion.span variants={line} custom={0} className="block pt-[0.08em] text-[14vw] sm:text-[11vw] lg:text-[9vw]">
              DJ
            </motion.span>
          </span>
          <span className="mask-reveal -mt-[0.12em]">
            <motion.span variants={line} custom={1} className="block pt-[0.12em] text-[17vw] text-gold sm:text-[13vw] lg:text-[11vw]">
              Código
            </motion.span>
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.7 }}
          data-testid="hero-tagline"
          className="mt-5 font-display text-xl font-semibold tracking-tight text-white sm:text-3xl"
        >
          {tagline}
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.75, duration: 0.7 }}
          className="mt-2 text-sm font-medium tracking-wide text-white/60 sm:text-base"
        >
          {genreLine || "Latin Urban • Reggaetón • Hip-Hop • Caribbean Club Music"}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}>
          <CampaignCta campaign={campaign} featured={featured} />
          {spotifyArtistUrl && (
            <button
              data-testid="hero-follow-spotify"
              onClick={() => followOnSpotify(spotifyArtistUrl)}
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-white/90 transition-transform active:scale-95 hover:border-[#1ED760]/60"
            >
              <SpotifyIcon size={16} /> Follow DJ Código on Spotify <ExternalLink size={13} />
            </button>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="mt-8 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40"
        >
          <ChevronDown size={16} className="animate-bounce text-gold" />
          Scroll to listen
        </motion.div>
      </motion.div>

      <div className="absolute bottom-0 left-0 right-0 z-10 border-y border-white/10 bg-ink/50 py-4 backdrop-blur-sm">
        <Marquee speed={40} gradient={false} autoFill>
          {["Latin Urban", "Reggaetón", "Hip-Hop", "Perreo", "Caribbean Club", "Dominican Roots", "Global Sound"].map((w, i) => (
            <span key={i} className="mx-8 font-display text-2xl font-semibold uppercase tracking-tight text-white/25 sm:text-3xl">
              {w} <span className="text-gold">✦</span>
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
}
